import { useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import { useAuth } from "../../../context/AuthContext";
import PaymentSlipUploadModal from "../payment/PaymentSlipUploadModal";
import { CheckCircle, AlertCircle, CreditCard, Building2 } from "lucide-react";
import { formatBDT } from "../types/formatCurrency";
import { useTranslation } from "react-i18next";

type GermanLevel = "A1" | "A2" | "B1" | "B2";

const INSTALLMENT_DETAILS: Record<
  string,
  { oneTime: number; installments: number; amount: number }
> = {
  "Ausbildung Preparatory Course": {
    oneTime: 156000,
    installments: 6,
    amount: 26000,
  },
  "Student Visa Processing Course": {
    oneTime: 54000,
    installments: 3,
    amount: 18000,
  },
  "Ausbildung Preparatory Course (Without German Language)": {
    oneTime: 125000,
    installments: 4,
    amount: 31250,
  },
  "Ausbildung Searching Visa Processing Course": {
    oneTime: 65000,
    installments: 2,
    amount: 32500,
  },
  "FSJ/BFD Preparatory Course": {
    oneTime: 65000,
    installments: 2,
    amount: 32500,
  },
  "Combo: Ausbildung Searching and Preparatory Course": {
    oneTime: 125000,
    installments: 4,
    amount: 31250,
  },
};

// One-time payment only courses
const ONE_TIME_COURSES: Record<string, number> = {
  "Au Pair Preparatory Course": 45000,
  "German Language Visa Preparatory Course": 45000,
};

const germanPrices: Record<GermanLevel, number> = {
  A1: 12500,
  A2: 13500,
  B1: 15500,
  B2: 17500,
};

// bKash agent Cash Out rate: BDT 13.95 per BDT 1,000. The student bears this
// charge on top of the course fee since it's a manual send-money payment, not
// a gateway integration — the business never receives the charge itself.
const BKASH_CHARGE_RATE_PER_1000 = 13.95;
const getBkashCharge = (amount: number): number =>
  Math.round((amount / 1000) * BKASH_CHARGE_RATE_PER_1000);

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  useAuth();
  const { t } = useTranslation();

  const storedCourse = localStorage.getItem("pendingCourse");
  const course =
    location.state?.course || (storedCourse ? JSON.parse(storedCourse) : null);

  const [paymentOption, setPaymentOption] = useState<"onetime" | "installment">(
    "onetime",
  );
  const [paymentMethod, setPaymentMethod] = useState<"bkash" | "bank">("bkash");
  const [selectedLevel, setSelectedLevel] = useState<GermanLevel>("A1");
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [transactionId, setTransactionId] = useState("");
  const [bankName, setBankName] = useState("");
  const [branchName, setBranchName] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isGermanCourse = course?.category === "German Language";
  const isAppointment = course?.category === "Appointment";

  const courseIdMap: Record<string, number> = {
    "course-001": 1,
    "course-002": 2,
    "course-003": 3,
    "course-004": 4,
    "course-005": 5,
    "course-006": 6,
    "course-007": 7,
    "course-008": 8,
    "course-009": 9,
  };
  const backendCourseId = course ? courseIdMap[course.id] : undefined;

  // Returns the amount to DISPLAY to the user
  const calculatePrice = (): number | string => {
    if (!course) return "N/A";

    if (isGermanCourse) {
      return germanPrices[selectedLevel];
    }

    if (ONE_TIME_COURSES[course.title]) {
      return ONE_TIME_COURSES[course.title];
    }

    const courseDetails = INSTALLMENT_DETAILS[course.title];
    if (!courseDetails) return "N/A";

    return paymentOption === "installment"
      ? courseDetails.amount // shows per-installment amount to user (e.g. 26,000)
      : courseDetails.oneTime; // shows full amount for one-time (e.g. 156,000)
  };

  // Returns the FULL TOTAL amount to send to the backend
  const getAmountForBackend = (): number => {
    if (isGermanCourse) {
      return germanPrices[selectedLevel];
    }

    if (ONE_TIME_COURSES[course.title]) {
      return ONE_TIME_COURSES[course.title];
    }

    const courseDetails = INSTALLMENT_DETAILS[course.title];
    if (!courseDetails) return 0;

    // Always send the full total — backend divides by installment count itself
    return courseDetails.oneTime;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (paymentMethod === "bkash" && !transactionId.trim()) {
      newErrors.transactionId = t("checkout.txnIdRequired");
    }

    if (paymentMethod === "bank" && !referenceNumber.trim()) {
      newErrors.referenceNumber = t("checkout.refNumRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceed = () => {
    if (validateForm()) {
      setShowUploadModal(true);
    } else {
      const firstError = Object.keys(errors)[0];
      document.getElementById(firstError)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  if (!course) return <Navigate to="/courses" replace />;

  const handleSlipSuccess = (_paymentId: string) => {
    setShowUploadModal(false);
    navigate("/student/dashboard");
  };

  const showInstallmentOption =
    !isGermanCourse &&
    !isAppointment &&
    course.allowInstallment &&
    !ONE_TIME_COURSES[course.title];

  const coursePriceNum = Number(calculatePrice()) || 0;
  const bkashCharge =
    paymentMethod === "bkash" ? getBkashCharge(coursePriceNum) : 0;
  const totalPayable = coursePriceNum + bkashCharge;

  return (
    <>
      <Breadcrumb title={course.title} />

      <PaymentSlipUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={handleSlipSuccess}
        paymentDetails={{
          paymentMethod,
          transactionId,
          bankName,
          branchName,
          referenceNumber,
          paymentDate: new Date().toISOString().split("T")[0],
          courseId: backendCourseId!,
          courseTitle: course.title,
          amount: getAmountForBackend(), // ✅ full total sent to backend (e.g. 156,000)
          displayAmount: Number(calculatePrice()), // ✅ per-installment shown to user (e.g. 26,000)
          paymentType: paymentOption,
          level: isGermanCourse ? selectedLevel : undefined,
        }}
      />

      <section className="bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              {t("checkout.title")}
            </h1>
            <p className="text-gray-600">
              {t("checkout.subtitle")}
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-12 space-x-4">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-[#009fe3] text-white flex items-center justify-center font-bold shadow-md">
                1
              </div>
              <span className="ml-2 text-sm font-medium text-gray-900">
                {t("checkout.step1")}
              </span>
            </div>
            <div className="w-16 h-1 bg-[#009fe3]"></div>
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-[#009fe3] text-white flex items-center justify-center font-bold shadow-md">
                2
              </div>
              <span className="ml-2 text-sm font-medium text-gray-900">
                {t("checkout.step2")}
              </span>
            </div>
            <div className="w-16 h-1 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-gray-300 text-gray-500 flex items-center justify-center font-bold">
                3
              </div>
              <span className="ml-2 text-sm font-medium text-gray-500">
                {t("checkout.step3")}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Course Info Card */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-[#009fe3] p-6">
                  <h3 className="text-2xl text-white font-bold mt-2">
                    {course.title}
                  </h3>
                </div>

                <div className="p-6">
                  {isGermanCourse && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        {t("checkout.selectLevel")}
                      </label>
                      <select
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition"
                        value={selectedLevel}
                        onChange={(e) =>
                          setSelectedLevel(e.target.value as GermanLevel)
                        }
                      >
                        <option value="A1">{t("checkout.a1Label")}</option>
                        <option value="A2">{t("checkout.a2Label")}</option>
                        <option value="B1">{t("checkout.b1Label")}</option>
                        <option value="B2">{t("checkout.b2Label")}</option>
                      </select>
                    </div>
                  )}

                  <div className="pt-3 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">
                        <strong>{t("checkout.coursePrice")}</strong>
                      </span>
                      <span className="text-2xl font-bold text-[#009fe3]">
                        {formatBDT(calculatePrice())} BDT
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Options Card */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                {showInstallmentOption && (
                  <div className="mb-6">
                    <h3 className="text-base font-bold text-gray-900 mb-3">
                      {t("checkout.paymentOption")}
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <label
                        className={`relative flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition ${
                          paymentOption === "onetime"
                            ? "border-[#009fe3] bg-[#009fe3] bg-opacity-5 shadow-sm"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          value="onetime"
                          checked={paymentOption === "onetime"}
                          onChange={() => setPaymentOption("onetime")}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <div className="font-semibold text-sm text-gray-900">
                            {t("checkout.oneTimePayment")}
                          </div>
                        </div>
                        {paymentOption === "onetime" && (
                          <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-[#009fe3]" />
                        )}
                      </label>

                      <label
                        className={`relative flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition ${
                          paymentOption === "installment"
                            ? "border-[#009fe3] bg-[#009fe3] bg-opacity-5 shadow-sm"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          value="installment"
                          checked={paymentOption === "installment"}
                          onChange={() => setPaymentOption("installment")}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <div className="font-semibold text-sm text-gray-900">
                            {t("checkout.installments")}
                          </div>
                        </div>
                        {paymentOption === "installment" && (
                          <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-[#009fe3]" />
                        )}
                      </label>
                    </div>
                  </div>
                )}

                {/* Payment Method */}
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-3">
                    {t("checkout.paymentMethod")}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <label
                      className={`relative flex flex-col items-center justify-center p-4 h-[76px] border-2 rounded-xl cursor-pointer transition ${
                        paymentMethod === "bkash"
                          ? "border-[#009fe3] bg-[#009fe3] bg-opacity-5 shadow-sm"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="radio"
                        value="bkash"
                        checked={paymentMethod === "bkash"}
                        onChange={() => setPaymentMethod("bkash")}
                        className="sr-only"
                      />
                      <CreditCard className="w-8 h-8 text-[#009fe3] mb-1" />
                      <div className="font-semibold text-sm text-gray-900">
                        bKash
                      </div>
                      {paymentMethod === "bkash" && (
                        <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-[#009fe3]" />
                      )}
                    </label>

                    <label
                      className={`relative flex flex-col items-center justify-center p-4 h-[76px] border-2 rounded-xl cursor-pointer transition ${
                        paymentMethod === "bank"
                          ? "border-[#009fe3] bg-[#009fe3] bg-opacity-5 shadow-sm"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="radio"
                        value="bank"
                        checked={paymentMethod === "bank"}
                        onChange={() => setPaymentMethod("bank")}
                        className="sr-only"
                      />
                      <Building2 className="w-8 h-8 text-[#009fe3] mb-1" />
                      <div className="font-semibold text-sm text-gray-900">
                        {t("checkout.bankTransfer")}
                      </div>
                      {paymentMethod === "bank" && (
                        <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-[#009fe3]" />
                      )}
                    </label>
                  </div>
                </div>
              </div>

              {/* Payment Instructions Card */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <h3 className="text-base font-bold text-gray-900 mb-4">
                  {t("checkout.paymentInstructions")}
                </h3>

                {paymentMethod === "bkash" ? (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-pink-50 to-purple-50 border-l-4 border-pink-500 rounded-xl p-5">
                      <div className="flex items-center">
                        <CreditCard className="w-6 h-6 text-pink-600 mr-3 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-600 mb-1">
                            {t("checkout.sendMoneyTo")}
                          </p>
                          <p className="text-2xl font-bold text-pink-600">
                            +8801645731253
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start bg-amber-50 border-l-4 border-amber-400 rounded-xl p-4">
                      <AlertCircle className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">
                        {t("checkout.bkashChargeHint")}
                      </p>
                    </div>

                    <div id="transactionId">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t("checkout.bkashTxnId")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., 9ABC12345"
                        value={transactionId}
                        onChange={(e) => {
                          setTransactionId(e.target.value);
                          if (errors.transactionId) {
                            setErrors({ ...errors, transactionId: "" });
                          }
                        }}
                        className={`w-full px-4 py-3 border-2 rounded-xl text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition ${
                          errors.transactionId
                            ? "border-red-500"
                            : "border-gray-200"
                        }`}
                      />
                      {errors.transactionId && (
                        <p className="text-red-500 text-xs mt-2 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.transactionId}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {t("checkout.bkashTxnHint")}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-xl p-5">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-gray-600">{t("checkout.accountName")}</p>
                          <p className="font-semibold text-gray-900">
                            MD Samsuddoha
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">{t("checkout.bank")}</p>
                          <p className="font-semibold text-gray-900">
                            BRAC Bank
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">{t("checkout.branch")}</p>
                          <p className="font-semibold text-gray-900">Rangpur</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">
                            {t("checkout.accountNumber")}
                          </p>
                          <p className="font-bold text-blue-600 text-base">
                            1069811730001
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center bg-green-50 border-l-4 border-green-400 rounded-xl p-3">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                      <p className="text-xs text-green-800 font-medium">
                        {t("checkout.bankNoCharge")}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t("checkout.yourBankName")}{" "}
                        <span className="text-gray-400 text-xs font-normal">
                          ({t("checkout.optional")})
                        </span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., BRAC Bank"
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t("checkout.yourBranchName")}{" "}
                        <span className="text-gray-400 text-xs font-normal">
                          ({t("checkout.optional")})
                        </span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., Dhaka"
                        value={branchName}
                        onChange={(e) => setBranchName(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition"
                      />
                    </div>

                    <div id="referenceNumber">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t("checkout.referenceNumber")} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., 123456789"
                        value={referenceNumber}
                        onChange={(e) => {
                          setReferenceNumber(e.target.value);
                          if (errors.referenceNumber) {
                            setErrors({ ...errors, referenceNumber: "" });
                          }
                        }}
                        className={`w-full px-4 py-3 border-2 rounded-xl text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition ${
                          errors.referenceNumber
                            ? "border-red-500"
                            : "border-gray-200"
                        }`}
                      />
                      {errors.referenceNumber && (
                        <p className="text-red-500 text-xs mt-2 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.referenceNumber}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {t("checkout.referenceHint")}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Payment Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-6">
                <h3 className="text-lg font-bold text-gray-900 mb-6">
                  {t("checkout.paymentSummary")}
                </h3>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>{t("checkout.courseFee")}</span>
                    <span className="font-medium">
                      {formatBDT(coursePriceNum)} BDT
                    </span>
                  </div>
                  {paymentMethod === "bkash" && (
                    <div className="flex justify-between text-pink-600">
                      <span>{t("checkout.bkashCharge")}</span>
                      <span className="font-medium">
                        + {formatBDT(bkashCharge)} BDT
                      </span>
                    </div>
                  )}
                  {paymentOption === "installment" && showInstallmentOption && (
                    <>
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>{t("checkout.paymentType")}</span>
                        <span>{t("checkout.firstInstallment")}</span>
                      </div>
                      <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded-lg">
                        {t("checkout.payInInstallments", {
                          count: INSTALLMENT_DETAILS[course.title]?.installments,
                        })}
                      </div>
                    </>
                  )}
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">
                        {t("checkout.totalPayable")}
                      </span>
                      <span className="text-2xl font-bold text-[#009fe3]">
                        {formatBDT(totalPayable)} BDT
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded-xl mb-6">
                  <div className="flex">
                    <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="ml-3">
                      <p className="text-sm font-semibold text-orange-800">
                        {t("checkout.important")}
                      </p>
                      <p className="text-xs text-orange-700 mt-1">
                        {t("checkout.importantNote")}
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleProceed}
                  className="w-full py-4 text-lg font-bold bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl shadow-lg hover:shadow-xl transition transform hover:-translate-y-0.5 flex items-center justify-center"
                >
                  <CheckCircle className="w-6 h-6 mr-2" />
                  {t("checkout.iHavePaid")}
                </button>

                <p className="text-xs text-center text-gray-500 mt-4">
                  {t("checkout.proceedDisclaimer")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Checkout;
